import { useQuery } from '@tanstack/react-query';
import { useAuth, PaymentService } from '@aaas/shared';

interface UseTransactionsParams {
  page?: number;
  page_size?: number;
  enabled?: boolean;
  enableAutoRefresh?: boolean;
}

/**
 * Hook to fetch workspace transaction history with pagination and auto-refresh
 * Auto-refresh only happens when enableAutoRefresh is true (e.g., on Billing tab)
 */
export const useTransactions = (params: UseTransactionsParams = {}) => {
  const { user, currentWorkspace } = useAuth();
  const { page = 1, page_size = 20, enabled = true, enableAutoRefresh = false } = params;

  return useQuery({
    queryKey: ['transactions', currentWorkspace?.id, page, page_size],
    queryFn: async () => {
      if (!user || !currentWorkspace) {
        throw new Error('User or workspace not available');
      }

      const idToken = await user.getIdToken();
      return PaymentService.getWorkspaceTransactions(idToken, currentWorkspace.id, {
        page,
        page_size,
      });
    },
    enabled: enabled && !!user && !!currentWorkspace,
    // Auto-refresh every 5 seconds if enabled (e.g., on Billing tab)
    refetchInterval: enableAutoRefresh ? 5000 : false,
    // Only refetch when tab is in focus (don't waste resources in background)
    refetchIntervalInBackground: false,
    staleTime: enableAutoRefresh ? 0 : 1000 * 60 * 2, // Always fresh if auto-refresh, else 2 min
  });
};
